// PLACAR "NAO QUIS INFORMAR" — a prestacao de contas da trava de captura ([DA-697], 09/09/2026).
//
// Ideia dele, e a frase carrega o desenho: *"as vendas com status de 'Nao quero Informar' pode
// aparecer em uma aba propria, para serem contabilizados e para que aja uma prestacao de contas
// por parte do vendedor"*. E' o freio que sustenta deixar a recusa a UM clique — sem alguem
// medindo, a saida mais barata da trava vira a saida padrao.
//
// ⛔ **Nasce PLACAR, nao fila.** As abas vizinhas listam pedido ABERTO, que alguem vai trabalhar e
// sair da lista. Aqui sao vendas FECHADAS: ninguem trabalha, nada esvazia, e em um mes sao ~350
// linhas. Por isso o quadro por vendedor vem primeiro e a lista desce embaixo, como comprovacao —
// lista que nao se resolve, sozinha, vira ruido que se aprende a ignorar ([DA-410]).
//
// ⚠️ **A barra mede o PERCENTUAL, nao a quantidade** — e isso e' o que faz o placar ser justo: a
// CRISTINA fecha 183 vendas no mes e a LANNA 2, entao em numero absoluto ela apareceria sempre
// como a pior. Barra cheia = 20%, e a escala vai escrita no cabecalho (numero sem rotulo afirma
// outra coisa).
//
// ⚠️ **Sem verde/vermelho** ([[user_daltonismo]]): o realce e' ambar `#b45309` sobre branco
// (contraste 5,0 medido) contra o cinza `#64748b` (4,8) — a diferenca e' de TOM, nao de matiz.
import { useCallback, useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { api } from '../../lib/api';
import { useToast } from '../../components/Toast';
import { BadgeEtiqueta } from '../atendimento/componentes/BadgeEtiqueta';

type Periodo = 'hoje' | 'semana' | 'mes';

interface LinhaVendedor { vendedor: string; vendas: number; recusas: number; pct: number }
interface EtiquetaRecusa { id: number; nome: string; cor: string; apelido: string | null }
interface LinhaVenda {
  numero: string; quando: string; vendedor: string; total: string; itens: number;
  /** Quem recusou ([DA-780]) — nulo/vazio nas recusas anteriores a 03/10/2026. */
  recusa_nome: string | null;
  recusa_etiquetas: EtiquetaRecusa[] | null;
}

// Pedido | Quando | Cliente | Etiqueta | Vendedor | Valor | Itens. ⚠️ `minmax(0,…)` nas elasticas:
// `1fr` puro tem piso no conteudo e a coluna de nome mais longo comeria a das etiquetas ([DA-602]).
const GRADE_VENDAS = 'grid-cols-[120px_100px_minmax(0,1.3fr)_minmax(0,1fr)_120px_110px_60px]';
interface Resposta {
  periodo: string;
  total_vendas: number;
  total_recusas: number;
  vendedores: LinhaVendedor[];
  linhas: LinhaVenda[];
}

// Barra cheia = 20% de recusa. Acima disso ela satura, e o numero ao lado segue dizendo o real.
const ESCALA_PCT = 20;
const LIMIAR_REALCE = 12;

const fmtBRL = (v: number | string) =>
  Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtPct = (v: number) => `${v.toFixed(1).replace('.', ',')}%`;

function dataCurta(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

const ROTULO: Record<Periodo, string> = { hoje: 'Hoje', semana: '7 dias', mes: 'Este mes' };

export function PlacarRecusas({ sinalRecarga }: {
  /** Sobe a cada clique no `Atualizar` da pagina — este placar carrega sozinho (por periodo) e nao
   *  e' alcancado pelo `carregar()` de fora ([DA-704], mesmo mecanismo da Agenda em [DA-684]). */
  sinalRecarga?: number;
} = {}) {
  const toast = useToast();
  const [periodo, setPeriodo] = useState<Periodo>('mes');
  const [dados, setDados] = useState<Resposta | null>(null);
  const [carregando, setCarregando] = useState(true);

  const carregar = useCallback(() => {
    setCarregando(true);
    api.get<Resposta>('/api/fila-retomada/recusas', { query: { periodo } })
      .then(setDados)
      .catch((e) => toast.erro(e?.corpo?.erro ?? 'Falha ao ler o placar'))
      .finally(() => setCarregando(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodo, sinalRecarga]);

  useEffect(() => { carregar(); }, [carregar]);

  const pctGeral = dados && dados.total_vendas > 0
    ? (dados.total_recusas / dados.total_vendas) * 100 : 0;

  return (
    <div className="pb-4">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className="text-xs text-gray-500">Periodo:</span>
        {(['hoje', 'semana', 'mes'] as Periodo[]).map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setPeriodo(p)}
            className={`px-3 py-1 rounded-full text-xs font-medium ${periodo === p ? 'btn-primario' : 'btn-secundario'}`}
          >
            {ROTULO[p]}
          </button>
        ))}
        <button
          type="button"
          onClick={carregar}
          className="btn-secundario px-2.5 py-1 rounded-lg text-xs flex items-center gap-1.5"
        >
          <RefreshCw size={12} className={carregando ? 'animate-spin' : ''} /> Atualizar
        </button>
        <div className="flex-1" />
        {dados && (
          <span className="text-xs text-gray-600">
            {dados.total_recusas} {dados.total_recusas === 1 ? 'recusa' : 'recusas'} em{' '}
            {dados.total_vendas} {dados.total_vendas === 1 ? 'venda' : 'vendas'} ·{' '}
            <strong>{fmtPct(pctGeral)}</strong>
          </span>
        )}
      </div>

      {/* Quadro por vendedor — o que a prestacao de contas usa. */}
      <div className="bg-white border border-gray-300 rounded-xl overflow-hidden mb-4">
        <div
          className="grid grid-cols-[150px_90px_90px_1fr] px-3 py-2 text-[11px] font-bold uppercase"
          style={{ backgroundColor: 'var(--grid-header-bg)', color: 'var(--grid-header-fg)' }}
        >
          <div>Vendedor</div>
          <div className="text-right">Recusas</div>
          <div className="text-right">Vendas</div>
          <div className="pl-3.5">% das vendas dele (barra cheia = {ESCALA_PCT}%)</div>
        </div>
        {!dados?.vendedores.length && !carregando && (
          <div className="px-3 py-3 text-sm text-gray-500">Nenhuma venda no periodo.</div>
        )}
        {dados?.vendedores.map((l) => {
          const realce = l.pct >= LIMIAR_REALCE;
          return (
            <div key={l.vendedor} className="grid grid-cols-[150px_90px_90px_1fr] items-center px-3 py-2 border-t border-gray-100 text-[13px]">
              <div className="font-semibold text-gray-800 truncate">{l.vendedor}</div>
              <div className="text-right font-bold text-gray-800">{l.recusas}</div>
              <div className="text-right text-gray-500">{l.vendas}</div>
              <div className="pl-3.5 flex items-center gap-2">
                <div className="flex-1 max-w-[280px] h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${Math.min(100, (l.pct / ESCALA_PCT) * 100)}%`,
                      backgroundColor: realce ? '#b45309' : '#64748b',
                    }}
                  />
                </div>
                <span className="text-xs font-semibold" style={{ color: realce ? '#b45309' : '#64748b' }}>
                  {fmtPct(l.pct)}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="text-[11px] font-bold text-gray-600 uppercase mb-1.5">As vendas, uma por uma</div>
      <div className="bg-white border border-gray-300 rounded-xl overflow-hidden">
        <div
          className={`grid ${GRADE_VENDAS} gap-x-3 px-3 py-2 text-[11px] font-bold uppercase`}
          style={{ backgroundColor: 'var(--grid-header-bg)', color: 'var(--grid-header-fg)' }}
        >
          <div>Pedido</div><div>Quando</div><div>Cliente</div><div>Etiqueta</div><div>Vendedor</div>
          <div className="text-right">Valor</div><div className="text-right">Itens</div>
        </div>
        {!dados?.linhas.length && !carregando && (
          <div className="px-3 py-3 text-sm text-gray-500">
            Nenhuma recusa registrada no periodo — toda venda saiu com o cliente identificado.
          </div>
        )}
        {dados?.linhas.map((l) => (
          <div key={l.numero} className={`grid ${GRADE_VENDAS} gap-x-3 items-center px-3 py-2 border-t border-gray-100 text-[13px]`}>
            <div className="font-mono font-bold text-blue-700">{l.numero}</div>
            <div className="text-gray-500">{dataCurta(l.quando)}</div>
            {/* Recusa anterior a 03/10/2026 nao pedia nome nem etiqueta: o traco diz "nao foi
                perguntado", nunca "Consumidor" — que afirmaria um nome que ninguem informou. */}
            <div className="text-gray-800 font-semibold truncate" title={l.recusa_nome ?? undefined}>
              {l.recusa_nome || <span className="text-gray-400 font-normal">—</span>}
            </div>
            <div className="flex flex-wrap gap-1 min-w-0">
              {l.recusa_etiquetas?.length
                ? l.recusa_etiquetas.map((e) => <BadgeEtiqueta key={e.id} nome={e.nome} cor={e.cor} apelido={e.apelido} />)
                : <span className="text-gray-400">—</span>}
            </div>
            <div className="text-gray-800 truncate">{l.vendedor}</div>
            <div className="text-right font-bold">R$ {fmtBRL(l.total)}</div>
            <div className="text-right text-gray-500">{l.itens}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
